import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { ROUTES } from 'config';
import {
  ActionsMenu,
  Button,
  ListTotal,
  Panel,
  SwitchInput,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import {
  COLOR_BY_STATUS,
  PartnerAdmin,
  useAdminDeletePartnerAdmin,
  useAdminDisablePartnerPortal,
  useAdminEnablePartnerPortal,
  useAdminOpenPartnerPortal,
  useAdminPartnerAdmins,
  useAdminSupplier,
} from 'store/admin-suppliers';
import { useRouteParams } from 'utils/useIdParam';
import { useModalContext } from 'store/modal';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import { PartnerAdminModal } from './atoms';

export const AdminSupplierPartnerPortal = () => {
  const { t } = useTranslation();
  const { id: supplierId } = useRouteParams(
    ROUTES.admin.suppliers.partnerPortal,
  );
  const { openModal } = useModalContext();
  const { confirmation } = useConfirm();
  const predefinedToasts = usePredefinedToasts();

  const { data: supplier } = useAdminSupplier(supplierId);
  const partnerPortalEnabled = Boolean(supplier?.partnerPortalEnabled);

  const { data: admins, isLoading } = useAdminPartnerAdmins(supplierId);

  const { mutate: enablePartnerPortal, isLoading: isEnabling } =
    useAdminEnablePartnerPortal();
  const { mutate: disablePartnerPortal, isLoading: isDisabling } =
    useAdminDisablePartnerPortal();
  const { mutate: deleteAdmin, isLoading: isDeleting } =
    useAdminDeletePartnerAdmin();
  const { mutate: openPartnerPortal, isLoading: isOpeningPortal } =
    useAdminOpenPartnerPortal();

  const isToggling = isEnabling || isDisabling;

  const handleToggle = async (enabled: boolean) => {
    if (enabled) {
      enablePartnerPortal({ supplierId });
      return;
    }

    const { confirmed } = await confirmation(
      t('marketplaceSuppliers.disablePartnerPortalConfirmation'),
    );

    if (confirmed) {
      disablePartnerPortal({ supplierId });
    }
  };

  const handleOpenPortal = () => {
    const tab = window.open('about:blank', '_blank');

    openPartnerPortal(
      { supplierId },
      {
        onSuccess: (response) => {
          const url = response.data.url;
          if (tab) {
            tab.location.href = url;
            return;
          }
          window.location.href = url;
        },
        onError: () => {
          tab?.close();
        },
      },
    );
  };

  const openAdminModal = (admin?: PartnerAdmin) => {
    openModal({
      component: PartnerAdminModal,
      props: { supplierId, admin },
    });
  };

  return (
    <Stack gap={2}>
      <Panel
        title={t('marketplaceSuppliers.partnerPortal', {
          defaultValue: 'Partner Portal',
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <SwitchInput
            label={t('marketplaceSuppliers.partnerPortalEnabled', {
              defaultValue: 'Enabled',
            })}
            value={partnerPortalEnabled}
            onChange={handleToggle}
            disabled={isToggling}
          />
          <Button
            label={t('marketplaceSuppliers.openPortal', {
              defaultValue: 'Open portal',
            })}
            variant="secondary"
            disabled={!partnerPortalEnabled}
            loading={isOpeningPortal}
            onClick={handleOpenPortal}
          />
        </Stack>
      </Panel>

      <TableWrapper>
        <TableToolbar>
          <Stack gap={1}>
            <Stack
              direction="row"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
            >
              <ActionsMenu
                type="spread"
                items={[
                  {
                    label: t('administrators.new_administrator'),
                    color: 'primary',
                    disabled: !partnerPortalEnabled,
                    onClick: () => openAdminModal(),
                  },
                ]}
              />
            </Stack>

            <Divider />

            <ListTotal total={admins?.length} />
          </Stack>
        </TableToolbar>
        <Table
          data={admins ?? []}
          isLoading={isLoading}
          columns={[
            {
              id: 'name',
              header: t('common.full_name'),
              cell: ({ row: { original: admin } }) => admin.fullName,
            },
            {
              id: 'email',
              header: t('common.email'),
              cell: ({ row: { original: admin } }) => admin.email,
            },
            {
              id: 'phone',
              header: t('form.mobile_number'),
              cell: ({ row: { original: admin } }) => admin.phone,
            },
            {
              id: 'root',
              header: t('marketplaceSuppliers.rootAdmin', {
                defaultValue: 'Root admin',
              }),
              cell: ({ row: { original: admin } }) =>
                admin.root ? t('common.yes') : t('common.no'),
            },
            {
              id: 'status',
              header: t('common.status'),
              cell: ({ row: { original: admin } }) => {
                return (
                  <Typography color={COLOR_BY_STATUS[admin.status]}>
                    {t(`status.${admin.status}`)}
                  </Typography>
                );
              },
            },
            {
              id: 'actions',
              cell: ({ row: { original: admin } }) => {
                return (
                  <ActionsMenu
                    type="compact"
                    items={[
                      {
                        label: t('common.edit'),
                        onClick: () => openAdminModal(admin),
                      },
                      {
                        label: t('actions.delete'),
                        color: 'error',
                        disabled: isDeleting,
                        onClick: async () => {
                          const { confirmed } = await confirmation(
                            t('common.areYouSureToDelete', {
                              title: admin.fullName || admin.email,
                            }),
                            undefined,
                            'danger',
                          );

                          if (confirmed) {
                            deleteAdmin(
                              { supplierId, adminId: admin.id },
                              {
                                onSuccess: () => {
                                  predefinedToasts.success.deleted();
                                },
                              },
                            );
                          }
                        },
                      },
                    ]}
                  />
                );
              },
              meta: { align: 'right' },
            },
          ]}
        />
      </TableWrapper>
    </Stack>
  );
};
